import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Trees, Shield } from 'lucide-react';
import { toastError, toastDismiss } from '../utils/toastHelper';
import { useTheme } from '../hooks/useTheme';
import CustomLoginForm from '../components/auth/CustomLoginForm';

const LoginPage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const { colors } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [showExpiredMessage, setShowExpiredMessage] = useState(false);

  // Verificar si viene de una sesión expirada
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const expired = urlParams.get('expired');

    if (expired === 'true') {
      setShowExpiredMessage(true);
      toastError('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', {
        duration: 5000,
        id: 'session-expired-login'
      });

      // Limpiar el parámetro de la URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location]);

  // Obtener la URL de retorno de los query params o del estado
  const returnUrl = location.state?.from?.pathname || '/admin';

  // Si ya está autenticado, redirigir a la URL de retorno
  if (user) {
    return <Navigate to={returnUrl} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{
      background: `linear-gradient(to bottom right, ${colors.background}, ${colors.surface})`
    }}>
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center" style={{
            backgroundColor: colors.primary
          }}>
            <Trees className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold" style={{ color: colors.text }}>
            Sistema de Camping
          </h2>
          <p className="mt-2 text-sm text-gray-600 flex items-center justify-center">
            <Shield className="h-4 w-4 mr-1" />
            Powered by UAU Auth
          </p>
        </div>

        {/* Mensaje de sesión expirada */}
        {showExpiredMessage && (
          <div className="p-4 rounded-lg" style={{
            backgroundColor: `${colors.warning}15`,
            border: `1px solid ${colors.warning}`
          }}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: colors.warning }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm" style={{ color: colors.warning }}>
                  Tu sesión ha expirado por seguridad. Por favor, inicia sesión nuevamente.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Login Form */}
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <CustomLoginForm
            onSuccess={() => {
              navigate(returnUrl, { replace: true });
            }}
            onError={(error) => {
              // Mostrar el mensaje de error específico del servidor
              toastDismiss('login-error');  // Limpiar toast anterior
              toastError(error, {
                duration: 4000,
                id: 'login-error'
              });
            }}
          />
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500">
          Sistema de Gestión de Camping - UAU Auth Package
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
